import { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Upload, DatePicker, App, Button, Space } from 'antd';
import type { UploadFile } from 'antd';
import { Upload as UploadIcon, X } from 'lucide-react';
import dayjs from 'dayjs';
import { documentService, type Document, type DocumentCategory } from '@/services/documentService';
import { orgService, type OrgNode } from '@/services/rolesService';
import { CategoryTreeSelect } from './CategoryTreeSelect';

interface Props {
  onClose: () => void;
  onCreated: (doc: Document) => void;
  /** Auto-fill khoa/phòng khi mở modal (vd từ sider đang chọn) */
  defaultDepartmentId?: string;
  /** Auto-fill danh mục */
  defaultCategoryId?: string;
}

// V1.0.65: 4 visibility levels
const CONFIDENTIALITY_OPTIONS = [
  { value: 'PUBLIC',       label: '🌐 Công khai — Mọi tài khoản đều xem được' },
  { value: 'INTERNAL',     label: '🏢 Nội bộ — Chỉ user thuộc khoa được map' },
  { value: 'SHARED',       label: '🔗 Chia sẻ — Chỉ tài khoản được chia sẻ' },
  { value: 'CONFIDENTIAL', label: '🔒 Mật — Chỉ chủ sở hữu + admin' },
];

export function CreateDocumentModal({ onClose, onCreated, defaultDepartmentId, defaultCategoryId }: Props) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [orgTree, setOrgTree] = useState<OrgNode[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    documentService.listCategories()
      .then(setCategories)
      .catch(() => message.error('Không tải được danh mục'));
    orgService.getTree()
      .then(setOrgTree)
      .catch(() => {/* silent */});
    form.setFieldsValue({
      confidentiality: 'INTERNAL',
      departmentIds: defaultDepartmentId ? [defaultDepartmentId] : [],
      categoryId: defaultCategoryId,
    });
  }, []);

  // Flatten orgTree for Select options
  // V1.0.47: Filter out khoa inactive (isActive === false) khỏi combobox
  const deptOptions = (() => {
    const out: { value: string; label: string }[] = [];
    const walk = (n: OrgNode, prefix: string) => {
      const isInactive = (n as any).isActive === false;
      if (!isInactive) {
        out.push({ value: n.id, label: `${prefix}${n.name}` });
      }
      n.children?.forEach(c => walk(c, prefix + (isInactive ? '' : '  ')));
    };
    orgTree.forEach(n => walk(n, ''));
    return out;
  })();

  const handleAddTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const file = fileList[0]?.originFileObj as File | undefined;
      // V1.0.31: deptIds là array; item đầu = primary, các item sau = additional
      const deptIds: string[] = Array.isArray(values.departmentIds)
        ? values.departmentIds.filter((d: any) => !!d)
        : [];
      const primaryDeptId = deptIds[0];
      const additionalDeptIds = deptIds.slice(1);

      const doc = await documentService.create({
        title: values.title,
        description: values.description,
        categoryId: values.categoryId,
        departmentId: primaryDeptId,
        additionalDepartmentIds: additionalDeptIds.length > 0 ? additionalDeptIds : undefined,
        confidentiality: values.confidentiality,
        tags: tags.length > 0 ? tags : undefined,
        effectiveFrom: values.effective?.[0]?.format('YYYY-MM-DD'),
        effectiveTo: values.effective?.[1]?.format('YYYY-MM-DD'),
        issuedDate: values.issuedDate?.format('YYYY-MM-DD'),
      }, file);
      message.success(`Đã tạo tài liệu ${doc.docCode}`);
      onCreated(doc);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi tạo tài liệu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title="Tạo tài liệu mới"
      onCancel={onClose}
      onOk={submit}
      confirmLoading={submitting}
      okText="Tạo tài liệu"
      cancelText="Hủy"
      width={640}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="title" label="Tiêu đề tài liệu"
          rules={[{ required: true, message: 'Bắt buộc' }, { max: 500 }]}>
          <Input placeholder="VD: Hợp đồng mua máy chủ backup Q2/2026" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item
            name="departmentIds"
            label="Khoa/Phòng được xem (chọn 1 hoặc nhiều)"
            rules={[{ required: true, message: 'Chọn ít nhất 1 khoa', type: 'array', min: 1 }]}
            extra="Khoa đầu tiên là khoa chủ quản. Tất cả khoa được chọn đều có thể xem tài liệu."
          >
            <Select
              mode="multiple"
              placeholder="Chọn khoa/phòng..."
              showSearch
              optionFilterProp="label"
              options={deptOptions}
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item name="categoryId" label="Loại tài liệu" rules={[{ required: true }]}>
            <CategoryTreeSelect
              categories={categories}
              placeholder="Chọn loại tài liệu..."
            />
          </Form.Item>
        </div>

        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="Mô tả ngắn về tài liệu..." />
        </Form.Item>

        <Form.Item label="Tags">
          <Space wrap style={{ marginBottom: 8 }}>
            {tags.map(t => (
              <span key={t} style={{
                padding: '2px 8px', background: '#e8f0fa', color: '#2f54eb',
                borderRadius: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {t}
                <X size={10} style={{ cursor: 'pointer' }}
                  onClick={() => setTags(tags.filter(x => x !== t))} />
              </span>
            ))}
          </Space>
          <Input
            placeholder="Nhập tag rồi Enter (VD: mua-sắm, 2026, quý-2)"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onPressEnter={(e) => { e.preventDefault(); handleAddTag(); }}
          />
        </Form.Item>

        <Form.Item name="confidentiality" label="Mức bảo mật" rules={[{ required: true }]}>
          <Select options={CONFIDENTIALITY_OPTIONS} />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="issuedDate" label="Ngày ban hành">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="effective" label="Hiệu lực (từ - đến)">
            <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </div>

        <Form.Item label="File tài liệu (tùy chọn)"
          extra="Có thể tạo tài liệu trống rồi upload file sau. Hỗ trợ PDF, DOCX, XLSX · Tối đa 100MB">
          <Upload.Dragger
            fileList={fileList}
            beforeUpload={() => false}
            maxCount={1}
            onChange={({ fileList }) => setFileList(fileList.slice(-1))}
            onRemove={() => { setFileList([]); return true; }}
          >
            <div style={{ padding: 16 }}>
              <UploadIcon size={24} style={{ color: '#6699CC' }} />
              <div style={{ marginTop: 8, fontSize: 12 }}>Kéo thả file hoặc bấm để chọn</div>
            </div>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
}
